import React, { useEffect } from 'react';
import styled from 'styled-components';

import { slideInFromLeftWeighted } from '../../shared/component/Animation';
import { BlackFitBtn, PaperBlurFitBtn } from '../../shared/component/headless/button/BtnBody';
import HeadlessBtn from '../../shared/component/headless/button/HeadlessBtn';
import { paperTextureModal } from '../../shared/component/paperTexture';
import { getBtnName } from '../../shared/constants/btnNames';
import { getModalMessage } from '../../shared/constants/modalMessages';
import { getPageMessage } from '../../shared/constants/pageMessages';
import { useProjectDetailState } from '../../shared/hooks/useApi/useProjectDetail';
import { useCanShowProjectDetail } from '../../shared/hooks/useProjectVisibility';
import { useImportStationWorkStore } from '../../shared/store/importStationWorkStore';
import { useModalStore } from '../../shared/store/portal/modalStore';
import { useModalWorkTagStore } from '../../shared/store/workTagStore';

import ImportTagStation from './ImportTagStation';
import PageAboutWorkImport from './PageAboutWorkImport';
import SimpleWorkForFormationItem from './SimpleWorkForFormationItem';
import FormationImportSelectionSlots from './FormationImportSelectionSlots';
import { clearFormationSlotImportModalState } from './useFormationSlotImport';
import { useFormationSlotImport } from './useFormationSlotImport';

const ImportStationForFormationSlot: React.FC = () => {
  const { isClosing, clearModal } = useModalStore();
  const { simpleList } = useImportStationWorkStore();
  const { project } = useProjectDetailState();
  const canShowProject = useCanShowProjectDetail();
  const { selectedModalTags } = useModalWorkTagStore();
  const {
    availableSlotIndexes,
    formationItems,
    formationType,
    importToEmptySlots,
    isBulkImport,
    selectedDataList,
  } = useFormationSlotImport();

  useEffect(() => () => {
    clearFormationSlotImportModalState();
  }, []);

  const handleCancel = () => {
    clearFormationSlotImportModalState();
    clearModal();
  };

  if (!canShowProject || !project) return null;

  return (
    <LeftModalFrame
      onClick={(e) => e.stopPropagation()}
      $isClosing={isClosing}
    >
      <ImportComp>
        <ImportMainTitleWrapper>
          <ImportTitle>
            Formation | {getModalMessage('SELECT_TO_IMPORT_WORK_DETAIL')}
            {isBulkImport && availableSlotIndexes.length > 0
              && ` (${selectedDataList.length}/${availableSlotIndexes.length})`}
          </ImportTitle>
        </ImportMainTitleWrapper>
        {isBulkImport && formationType && (
          <FormationImportSelectionSlots
            formationItems={formationItems}
            formationType={formationType}
          />
        )}
        <ImportTagStation />
        <ImportContentContainer>
          {simpleList.length === 0 ? (
            <NoContentContainer>
              {selectedModalTags.length > 0
                ? getPageMessage('IMPORT_STATION_NO_WORK_FOR_SELECTED_TAGS')
                : getPageMessage('IMPORT_STATION_EMPTY_WORK')}
            </NoContentContainer>
          ) : (
            simpleList.map((sw) => (
              <SimpleContentComp key={sw.id}>
                <SimpleWorkForFormationItem data={sw} />
              </SimpleContentComp>
            ))
          )}
        </ImportContentContainer>
        <PageAboutWorkImport />
        {isBulkImport && (
          <ButtonContainer>
            {availableSlotIndexes.length > 0 && (
              <HeadlessBtn
                value={getBtnName('IMPORT')}
                handleClick={importToEmptySlots}
                StyledBtn={BlackFitBtn}
              />
            )}
            <HeadlessBtn
              value={getBtnName('CANCEL')}
              handleClick={handleCancel}
              StyledBtn={PaperBlurFitBtn}
            />
          </ButtonContainer>
        )}
      </ImportComp>
    </LeftModalFrame>
  );
};

const LeftModalFrame = styled.div<{ $isClosing: boolean }>`
  position: absolute;
  left: 4vw;
  width: calc(40vw + 40px);
  min-width: 500px;
  max-width: 600px;
  height: 75vh;
  padding: 20px;
  opacity: ${({ $isClosing }) => ($isClosing ? 0 : 1)};
  transform: ${({ $isClosing }) => ($isClosing ? 'translateX(-100%)' : 'translateX(0)')};
  transition: all 0.6s ease-out;
  ${paperTextureModal}
  ${slideInFromLeftWeighted}
`;

const ImportComp = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 8px;
`;

const ImportMainTitleWrapper = styled.div`
  width: 100%;
  height: 5vh;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
`;

const ImportTitle = styled.div`
  display: flex;
  justify-content: flex-start;
  ${({ theme }) => theme.typography.Body_02_1};
`;

const ImportContentContainer = styled.div`
  width: 100%;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  overflow-y: scroll;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 4px;
`;

const NoContentContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: ${({ theme }) => theme.colors.color_Gray_02};
  ${({ theme }) => theme.typography.Body_03_2};
  white-space: pre-line;
`;

const SimpleContentComp = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

export default ImportStationForFormationSlot;
